import React, { useCallback, useEffect, useReducer, useRef, useState } from 'react';

import { ScrollView } from '@rbilabs/universal-components';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useStoreContext } from 'features/store';
import {
  SelectDeliveryStoreStatus,
  useSelectDeliveryStore,
} from 'features/store/hooks/use-select-delivery-store';
import { useSelectStore } from 'features/store/hooks/use-select-store';
import { addressReducer } from 'features/store/pages/delivery-store-selector/reducer';
import { View } from 'react-native';

import { CardModal } from 'components/card-modal';
import { IDeliveryAddress, ServiceMode } from 'generated/graphql-gateway';
import usePlaceIdDetails from 'hooks/geolocation/use-place-id-details';
import useErrorModal from 'hooks/use-error-modal';
import { CustomEventNames, logNavigationClick } from 'state/amplitude';
import { useAuthContext } from 'state/auth';
import { t } from 'state/intl/translations';
import { useOrderContext } from 'state/order';
import { primitive } from 'styles/constants';
import { toError } from 'utils/errors';
import { IPlaceAddress, IPlaceData } from 'utils/geolocation';
import { routes } from 'utils/routing';

import { DeliveryAddressSelectHandle, DeliveryAddresses } from './delivery-addresses';
import { DeliveryUnavailable } from './delivery-unavailable';
import { useHeaderTitle } from './hooks/use-header-title';
import { ModalFooter } from './modal-footer';
import { DeliveryAddressForm, DeliveryAddressFormHandle } from './new-address-form';
import { saveDeliveryAddressIfRequested } from './save-delivery-address-if-requested';
import { DeliveryModalContainer } from './styled';
import { ActionStatus, SelectedDeliveryAddress } from './types';
import { getAddressLabel } from './utils';

export const DeliveryStoreSelectorModal = () => {
  const router = useRouter();
  const { redirectUrl } = useLocalSearchParams<{ redirectUrl: keyof typeof routes }>();
  const selectDeliveryStore = useSelectDeliveryStore();

  const [state, dispatch] = useReducer(addressReducer, {
    error: null,
    address: null,
    status: ActionStatus.IDLE,
  });

  const { StoreChangeDialog, setIsSelectingStore } = useStoreContext();

  const {
    user,
    editDeliveryAddress,
    removeDeliveryAddress,
    saveDeliveryAddress,
  } = useAuthContext();

  const [createAddressView, _setCreateAddressView] = useState(false);
  const [interceptedClose, setInterceptedClose] = useState(false);
  const [isNewAddress, setIsNewAddress] = useState(false);
  const [addressBeingEdited, setAddressBeingEdited] = useState<IDeliveryAddress | null>(null);
  const [selected, setSelected] = useState('');
  const [addressComponents, setAddressComponents] = useState<IPlaceAddress | null>(null);
  const [message, setMessage] = useState('');
  const [formHasError, setFormHasError] = useState(false);
  const [shouldSaveInfoChecked, setShouldSaveInfoChecked] = useState(false);
  const [alias, setAlias] = useState<string>('');
  const [showTermsView, setShowTermsView] = useState(false);
  const [showtermsLink, setShowtermsLink] = useState(false);
  const selectStore = useSelectStore();

  const getDetails = usePlaceIdDetails();

  const {
    setDeliveryInstructions,
    deliveryInstructions,
    setShouldSaveDeliveryAddress,
    setFormShouldSaveDeliveryAddress,
    setOrderPhoneNumber,
    updateShouldSaveDeliveryAddress,
  } = useOrderContext();

  const showDeliveryAddresses = Boolean(user?.details?.deliveryAddresses?.length);
  const showAddresses = showDeliveryAddresses && !createAddressView;
  // to avoid flatLists nested in a scrollView or else react-native complains
  const Container = showAddresses ? ScrollView : View;
  const formRef = useRef<DeliveryAddressFormHandle>(null);
  const selectRef = useRef<DeliveryAddressSelectHandle>(null);
  const isDeliveryAddressSubmitDisabled = !addressComponents?.addressLine1 || formHasError;
  const shouldUseKeyboardAwareFooter =
    !isDeliveryAddressSubmitDisabled &&
    !showtermsLink &&
    !showTermsView &&
    state.status !== ActionStatus.QUOTE_UNAVAILABLE;

  const header = useHeaderTitle({
    status: state.status,
    addressBeingEdited: !!addressBeingEdited,
    showDeliveryAddresses,
    createAddressView,
    showTermsView,
  });

  const [ErrorDialog, openErrorDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Delivery Not Completed',
  });

  const syncSaveDeliveryPreference = useCallback(
    (shouldSave: boolean) => {
      setShouldSaveDeliveryAddress(shouldSave);
      setFormShouldSaveDeliveryAddress(shouldSave);
      updateShouldSaveDeliveryAddress(shouldSave);
    },
    [
      setFormShouldSaveDeliveryAddress,
      setShouldSaveDeliveryAddress,
      updateShouldSaveDeliveryAddress,
    ]
  );

  const handleShouldSaveInfoChange = useCallback(
    (shouldSave: boolean) => {
      setShouldSaveInfoChecked(shouldSave);
      syncSaveDeliveryPreference(shouldSave);
    },
    [syncSaveDeliveryPreference]
  );

  const completeDelivery = async (data: SelectedDeliveryAddress, savedAddress = false) => {
    setIsSelectingStore(true);
    if (state.status === ActionStatus.PENDING) {
      return;
    }

    if (!data.address) {
      return dispatch({
        type: ActionStatus.ERROR,
        error: { message: 'Missing address information for delivery' },
      });
    }

    setIsNewAddress(!savedAddress);

    dispatch({ type: ActionStatus.PENDING, address: data });

    try {
      const { status, restaurant, phoneNumber } = await selectDeliveryStore({
        ...data,
        alias: data.address.alias || alias || '',
        isNewAddress,
      });

      if (status === SelectDeliveryStoreStatus.QUOTE_ERROR) {
        return dispatch({
          type: ActionStatus.ERROR,
          error: {
            message: t('pleaseTryAgainLater'),
            modalAppearanceEventMessage: 'Error: Delivery Quote Error',
          },
        });
      }

      if (status === SelectDeliveryStoreStatus.QUOTE_UNAVAILABLE) {
        return dispatch({ type: ActionStatus.QUOTE_UNAVAILABLE });
      }

      if (status === SelectDeliveryStoreStatus.UNRECOGNIZED_ADDRESS) {
        return dispatch({
          type: ActionStatus.ERROR,
          error: {
            message: 'Error using delivery address',
            error: {
              name: SelectDeliveryStoreStatus.UNRECOGNIZED_ADDRESS,
              message: 'Unable to get coordinates from selected address',
            },
          },
        });
      }

      syncSaveDeliveryPreference(shouldSaveInfoChecked);

      if (shouldSaveInfoChecked && status === SelectDeliveryStoreStatus.QUOTE_SUCCESSFUL) {
        const didSave = await saveDeliveryAddressIfRequested({
          shouldSave: shouldSaveInfoChecked,
          user,
          address: {
            ...data.address,
            alias: data.address.alias || alias || '',
            defaultInstruction: data.address.defaultInstruction ?? deliveryInstructions,
          },
          phoneNumber: phoneNumber ?? data.phoneNumber ?? '',
          saveDeliveryAddress,
        });

        if (didSave) {
          syncSaveDeliveryPreference(false);
        }
      }

      setDeliveryInstructions(data.address.defaultInstruction ?? deliveryInstructions);

      if (status === SelectDeliveryStoreStatus.QUOTE_SUCCESSFUL && restaurant) {
        await selectStore({
          newStore: restaurant,
          requestedServiceMode: ServiceMode.DELIVERY,
          callback: () => {
            setOrderPhoneNumber(phoneNumber ?? '');
            router.replace(redirectUrl || routes.menu);
          },
        });
      }
      dispatch({ type: ActionStatus.SUCCESS });
    } catch (error) {
      dispatch({
        type: ActionStatus.ERROR,
        error: { message: 'Error using delivery address', error: toError(error) },
      });
    } finally {
      setIsSelectingStore(false);
    }
  };

  const getDetailsPlaceData = useCallback(
    async (pId: string | undefined): Promise<IPlaceData> => {
      return getDetails(pId).then(placeData => {
        if (placeData) {
          return Promise.resolve(placeData);
        }

        logNavigationClick(CustomEventNames.BUTTON_CLICK_DELIVER_HERE, {
          error: 'Address Not Recognized',
        });

        return Promise.reject(placeData);
      });
    },
    [getDetails]
  );

  useEffect(() => {
    if (state.status === ActionStatus.ERROR && state.error) {
      openErrorDialog(state.error);
    }
  }, [openErrorDialog, state.error, state.status]);

  const handleModalClose = () => {
    const hasChanges = formRef.current?.hasUnsavedChanges?.();

    if (showTermsView) {
      setShowTermsView(false);
      return;
    }

    if (hasChanges) {
      setInterceptedClose(true);
    } else {
      _setCreateAddressView(false);
      setAddressBeingEdited(null);
      router.dismiss();
    }
  };

  return (
    <CardModal
      headerText={header}
      onDismiss={handleModalClose}
      footerBackgroundColor={!showtermsLink ? primitive.$white : primitive.$mayoEggWhite}
      disableFooterBorder={showtermsLink}
      analyticsEvent={'Select your delivery address'}
      scrollView={true}
      noDesktopWebMaxWidth={true}
      keyboardAwareFooter={shouldUseKeyboardAwareFooter}
      footer={
        <ModalFooter
          status={state.status}
          selected={selected}
          selectRef={selectRef}
          formRef={formRef}
          showDeliveryAddresses={showDeliveryAddresses}
          createAddressView={createAddressView}
          isEditing={!!addressBeingEdited}
          isDisabled={isDeliveryAddressSubmitDisabled}
          showTermsLink={showtermsLink}
          showTermsView={showTermsView}
        />
      }
    >
      <Container style={{ flex: 1 }} testID="delivery-modal">
        {state.status === ActionStatus.QUOTE_UNAVAILABLE && (
          <DeliveryUnavailable
            address={state.address ? getAddressLabel(state.address) : ''}
            close={() => dispatch({ type: ActionStatus.IDLE })}
          />
        )}

        {state.status !== ActionStatus.QUOTE_UNAVAILABLE && (
          <DeliveryModalContainer testID="delivery-modal-container">
            {showAddresses ? (
              <DeliveryAddresses
                ref={selectRef}
                addresses={user?.details?.deliveryAddresses || []}
                onCreate={() => {
                  _setCreateAddressView(true);
                  setAddressBeingEdited(null);
                }}
                selected={selected}
                setSelected={setSelected}
                setAddress={address => completeDelivery(address, false)}
                isLoading={state.status === ActionStatus.PENDING}
                removeDeliveryAddress={removeDeliveryAddress}
                onEditAddress={addr => {
                  setAddressBeingEdited(addr);
                  handleShouldSaveInfoChange(true);
                  _setCreateAddressView(true);
                }}
                setShowTermsView={setShowTermsView}
              />
            ) : (
              <DeliveryAddressForm
                ref={formRef}
                instructions={deliveryInstructions}
                getDetailsPlaceData={getDetailsPlaceData}
                setCreateAddressView={_setCreateAddressView}
                showDeliveryAddresses={showDeliveryAddresses}
                user={user}
                setAddress={completeDelivery}
                isLoading={state.status === ActionStatus.PENDING}
                addressToEdit={addressBeingEdited}
                editDeliveryAddress={editDeliveryAddress}
                addressComponents={addressComponents}
                setAddressComponents={setAddressComponents}
                message={message}
                setMessage={setMessage}
                clearAddressBeingEdited={() => setAddressBeingEdited(null)}
                deliveryAddresses={user?.details?.deliveryAddresses || []}
                setFormHasError={setFormHasError}
                shouldSaveInfoChecked={shouldSaveInfoChecked}
                setShouldSaveInfoChecked={handleShouldSaveInfoChange}
                alias={alias}
                setAlias={setAlias}
                interceptedClose={interceptedClose}
                setInterceptedClose={setInterceptedClose}
                dispatch={dispatch}
                setShowtermsLink={setShowtermsLink}
              />
            )}
          </DeliveryModalContainer>
        )}

        <StoreChangeDialog heading={t('changeStores')} />

        <ErrorDialog testID="address-modal-error-dialog" />
      </Container>
    </CardModal>
  );
};
